import React, { useEffect, useState } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { API_DOCS } from './RegistriContabiliPage.api';
import './SezionaliRegistri.css';

// In Italia il libro giornale è UNICO con numerazione progressiva univoca
// (art. 2216 c.c.). BOP gestisce perciò un solo registro (il Giornale) per
// società, creato automaticamente e mostrato in SOLA LETTURA. Cassa e banca
// non sono registri ma conti del piano dei conti (sottotipo CASSA/BANCA); il
// collegamento alla Banca aziendale vive sul sottoconto.

interface RegistroContabile {
  id: number;
  societaId: number;
  codice: string;
  descrizione: string;
  ultimoNumero: number;
  ultimoAnno: number;
  attivo: boolean;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function RegistriContabiliPage() {
  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [items, setItems] = useState<RegistroContabile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    if (!societaId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      // @api GET /api/registri-contabili?societaId= -> { success: true, data: [{ id, societaId, codice, descrizione, ultimoNumero, ultimoAnno, attivo, note }] }
      const res = await apiFetch<{ success: boolean; data: RegistroContabile[] }>(`/api/registri-contabili?societaId=${societaId}`);
      setItems(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId]);

  const noSocieta = !societaId;

  return (
    <Layout>
      <div className="sezionali-registri">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Registro contabile</h2>
            <p>Libro giornale unico della società (sola lettura)</p>
          </div>
        </div>

        <div className="sr-callout">
          <p className="sr-callout__title">Perché un solo registro?</p>
          <p className="sr-callout__text">
            In Italia il <strong>libro giornale è unico</strong> con numerazione
            progressiva univoca (art. 2216 c.c.). BOP crea e gestisce
            automaticamente un solo Giornale per società: non è prevista la
            creazione di più registri. Cassa e banca non sono registri ma
            <strong> conti</strong> del piano dei conti (sottotipo Cassa/Banca);
            il collegamento alla Banca aziendale si imposta sul singolo
            sottoconto, in <strong>Piano dei conti CO.GE</strong>.
          </p>
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        <div className="bop-card">
          <div className="bop-table-wrapper">
            {noSocieta ? (
              <div className="bop-empty-state">Seleziona una società per vedere il suo libro giornale.</div>
            ) : loading ? (
              <div className="bop-empty-state">Caricamento…</div>
            ) : items.length === 0 ? (
              <div className="bop-empty-state">Nessun registro: verrà creato automaticamente al primo utilizzo.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>Numerazione corrente</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(r => (
                    <tr key={r.id} className={r.attivo ? '' : 'sr-row--inactive'}>
                      <td className="sr-codice">{r.codice}</td>
                      <td>
                        <div className="sr-descrizione-cell">
                          <span>{r.descrizione}</span>
                          {r.note && <span className="sr-note">{r.note}</span>}
                        </div>
                      </td>
                      <td>
                        {r.ultimoNumero > 0
                          ? <span className="sr-numerazione">{r.ultimoNumero}/{r.ultimoAnno}</span>
                          : <span className="bop-text-muted">non ancora utilizzato</span>}
                      </td>
                      <td>
                        <span className={`bop-badge ${r.attivo ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
                          {r.attivo ? 'Attivo' : 'Disattivo'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
